import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  ListItemIcon,
  Avatar,
  Badge,
  Chip,
  Divider,
  Grid,
  Paper,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
  FormControlLabel,
  Switch,
  Tooltip,
  Stack,
} from '@mui/material';
import {
  Email as EmailIcon,
  Sms as SmsIcon,
  Phone as PhoneIcon,
  WhatsApp as WhatsAppIcon,
  Message as PortalIcon,
  Send as SendIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Delete as DeleteIcon,
  Reply as ReplyIcon,
  MoreVert as MoreVertIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Settings as SettingsIcon,
  Add as AddIcon,
  AttachFile as AttachFileIcon,
  Close as CloseIcon,
  AccessTime as TimeIcon,
  CheckCircle as DeliveredIcon,
  DoneAll as ReadIcon,
  Error as ErrorIcon,
  Circle as CircleIcon,
  Description as TemplateIcon,
} from '@mui/icons-material';
import CommunicationsService, {
  Communication,
  CommunicationThread,
  CommunicationTemplate,
  CommunicationPreference,
  CommunicationStatistics,
  CommunicationChannel,
  CommunicationStatus,
  CreateCommunicationRequest,
} from '../../services/CommunicationsService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`communications-tabpanel-${index}`}
      aria-labelledby={`communications-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

/**
 * Communications Component for Project Manager
 * Multi-channel communication management (Email, SMS, Phone, WhatsApp, Portal)
 */
const Communications: React.FC = () => {
  // State
  const [communications, setCommunications] = useState<Communication[]>([]);
  const [threads, setThreads] = useState<CommunicationThread[]>([]);
  const [templates, setTemplates] = useState<CommunicationTemplate[]>([]);
  const [preferences, setPreferences] = useState<CommunicationPreference | null>(null);
  const [statistics, setStatistics] = useState<CommunicationStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // UI State
  const [activeTab, setActiveTab] = useState(0);
  const [selectedChannel, setSelectedChannel] = useState<CommunicationChannel | 'all'>('all');
  const [selectedThread, setSelectedThread] = useState<CommunicationThread | null>(null);
  const [selectedCommunication, setSelectedCommunication] = useState<Communication | null>(null);
  const [composeDialogOpen, setComposeDialogOpen] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [preferencesDialogOpen, setPreferencesDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  
  // Compose Form State
  const [composeData, setComposeData] = useState<Partial<CreateCommunicationRequest>>({
    channel: 'email',
    priority: 'normal',
  });
  const [attachments, setAttachments] = useState<File[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<CommunicationTemplate | null>(null);
  
  // Dropdown State
  const [clients, setClients] = useState<Array<{ id: number; name: string; email: string }>>([]);
  const [projects, setProjects] = useState<Array<{ id: number; name: string; status: string }>>([]);
  const [recipients, setRecipients] = useState<Array<{ id: number; name: string; email: string; role: string }>>([]);
  
  // Menu State
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [contextItem, setContextItem] = useState<Communication | null>(null);

  // Load data
  useEffect(() => {
    loadData();
  }, [activeTab, selectedChannel, showArchived, showUnreadOnly]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const filters: any = {};
      if (selectedChannel !== 'all') filters.channel = selectedChannel;
      if (showArchived) filters.is_archived = true;
      if (showUnreadOnly) filters.is_read = false;

      const [commsData, threadsData, templatesData, prefsData, statsData] = await Promise.all([
        CommunicationsService.getCommunications(filters),
        CommunicationsService.getThreads(),
        CommunicationsService.getTemplates(),
        CommunicationsService.getPreferences(),
        CommunicationsService.getStatistics(),
      ]);

      setCommunications(commsData.data);
      setThreads(Array.isArray(threadsData) ? threadsData : []);
      setTemplates(Array.isArray(templatesData) ? templatesData : []);
      setPreferences(prefsData);
      setStatistics(statsData);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load communications');
      console.error('Error loading communications:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle compose
  const handleCompose = async (channel?: CommunicationChannel, threadId?: number) => {
    setComposeData({
      channel: channel || 'email',
      priority: 'normal',
      thread_id: threadId,
    });
    
    // Load clients when opening compose dialog
    try {
      const clientsData = await CommunicationsService.getClients();
      setClients(clientsData);
    } catch (err: any) {
      console.error('Error loading clients:', err);
      setError('Failed to load clients');
    }
    
    setComposeDialogOpen(true);
  };

  // Handle client selection - load projects and recipients
  const handleClientChange = async (clientId: number) => {
    setComposeData({
      ...composeData,
      client_id: clientId,
      project_id: undefined,
      recipient_id: undefined,
    });
    
    try {
      const [projectsData, recipientsData] = await Promise.all([
        CommunicationsService.getClientProjects(clientId),
        CommunicationsService.getClientUsers(clientId),
      ]);
      
      setProjects(projectsData);
      setRecipients(recipientsData);
    } catch (err: any) {
      console.error('Error loading client data:', err);
      setError('Failed to load client projects and users');
    }
  };

  const handleSendCommunication = async () => {
    try {
      if (!composeData.recipient_id || !composeData.message) {
        setError('Please select a recipient and enter a message');
        return;
      }

      setLoading(true);
      let result;

      if (attachments.length > 0 && composeData.channel === 'email') {
        result = await CommunicationsService.sendCommunicationWithAttachments(
          composeData as CreateCommunicationRequest,
          attachments
        );
      } else {
        result = await CommunicationsService.sendCommunication(composeData as CreateCommunicationRequest);
      }

      setSuccess(result.message);
      setComposeDialogOpen(false);
      setComposeData({ channel: 'email', priority: 'normal' });
      setAttachments([]);
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to send communication');
      console.error('Error sending communication:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle template
  const handleUseTemplate = (template: CommunicationTemplate) => {
    setComposeData(prev => ({
      ...prev,
      channel: template.channel,
      subject: template.subject || undefined,
      message: template.message,
      html_message: template.html_message || undefined,
    }));
    setSelectedTemplate(template);
    setTemplateDialogOpen(false);
  };

  // Handle actions
  const handleMarkAsRead = async (id: number) => {
    try {
      await CommunicationsService.markAsRead(id);
      setSuccess('Marked as read');
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to mark as read');
    }
  };

  const handleToggleArchive = async (id: number) => {
    try {
      await CommunicationsService.toggleArchive(id);
      setSuccess('Updated successfully');
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this communication?')) return;
    
    try {
      await CommunicationsService.deleteCommunication(id);
      setSuccess('Communication deleted');
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete');
    }
  };

  const handleReply = (communication: Communication) => {
    setComposeData({
      project_id: communication.project_id,
      client_id: communication.client_id,
      recipient_id: communication.sender_id,
      channel: communication.channel,
      thread_id: communication.thread_id || undefined,
      parent_id: communication.id,
      subject: communication.subject ? `Re: ${communication.subject}` : undefined,
      priority: 'normal',
    });
    setComposeDialogOpen(true);
  };

  const handleCloseThread = async (threadId: number) => {
    try {
      await CommunicationsService.closeThread(threadId);
      setSuccess('Thread closed');
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to close thread');
    }
  };

  // Utility functions
  const getChannelIcon = (channel: CommunicationChannel) => {
    switch (channel) {
      case 'email': return <EmailIcon />;
      case 'sms': return <SmsIcon />;
      case 'phone': return <PhoneIcon />;
      case 'whatsapp': return <WhatsAppIcon />;
      case 'portal': return <PortalIcon />;
      default: return <EmailIcon />;
    }
  };

  const getChannelColor = (channel: CommunicationChannel) => {
    switch (channel) {
      case 'email': return 'primary';
      case 'sms': return 'secondary';
      case 'phone': return 'success';
      case 'whatsapp': return 'info';
      case 'portal': return 'warning';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: CommunicationStatus) => {
    switch (status) {
      case 'draft': return <CircleIcon fontSize="small" />;
      case 'sent': return <SendIcon fontSize="small" />;
      case 'delivered': return <DeliveredIcon fontSize="small" />;
      case 'read': return <ReadIcon fontSize="small" />;
      case 'failed': return <ErrorIcon fontSize="small" />;
      case 'pending': return <TimeIcon fontSize="small" />;
      default: return <CircleIcon fontSize="small" />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'error';
      case 'high': return 'warning';
      case 'normal': return 'default';
      case 'low': return 'info';
      default: return 'default';
    }
  };

  // Render Statistics
  const renderStatistics = () => (
    <Grid container spacing={3}>
      <Grid size={{ xs: 12, md: 3 }}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>Total Communications</Typography>
            <Typography variant="h4">{statistics?.total || 0}</Typography>
            <Typography variant="body2" color="textSecondary">
              {statistics?.recent_7_days || 0} in last 7 days
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid size={{ xs: 12, md: 3 }}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>Unread Messages</Typography>
            <Typography variant="h4" color="primary">{statistics?.unread || 0}</Typography>
            <Typography variant="body2" color="textSecondary">
              Sent: {statistics?.sent || 0} | Received: {statistics?.received || 0}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid size={{ xs: 12, md: 3 }}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>Active Threads</Typography>
            <Typography variant="h4">{statistics?.threads?.active || 0}</Typography>
            <Typography variant="body2" color="textSecondary">
              Closed: {statistics?.threads?.closed || 0}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid size={{ xs: 12, md: 3 }}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>By Channel</Typography>
            <Stack spacing={0.5}>
              <Typography variant="body2">Email: {statistics?.by_channel?.email || 0}</Typography>
              <Typography variant="body2">SMS: {statistics?.by_channel?.sms || 0}</Typography>
              <Typography variant="body2">Phone: {statistics?.by_channel?.phone || 0}</Typography>
              <Typography variant="body2">WhatsApp: {statistics?.by_channel?.whatsapp || 0}</Typography>
              <Typography variant="body2">Portal: {statistics?.by_channel?.portal || 0}</Typography>
            </Stack>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  // Render Threads List
  const renderThreadsList = () => (
    <List>
      {threads.map((thread) => (
        <React.Fragment key={thread.id}>
          <ListItemButton
            selected={selectedThread?.id === thread.id}
            onClick={() => setSelectedThread(thread)}
          >
            <ListItemAvatar>
              <Badge badgeContent={thread.unread_count} color="primary">
                <Avatar>{getChannelIcon(thread.primary_channel)}</Avatar>
              </Badge>
            </ListItemAvatar>
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle1">{thread.subject}</Typography>
                  <Chip label={thread.status} size="small" />
                </Box>
              }
              secondary={
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    {thread.message_count} messages • Last: {new Date(thread.last_message_at || '').toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    By: {thread.last_sender_name}
                  </Typography>
                </Box>
              }
            />
            <IconButton onClick={() => handleCloseThread(thread.id)}>
              <CloseIcon />
            </IconButton>
          </ListItemButton>
          <Divider />
        </React.Fragment>
      ))}
    </List>
  );

  // Render Communications List
  const renderCommunicationsList = () => {
    const filteredComms = communications.filter(comm =>
      searchQuery === '' ||
      comm.subject?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      comm.message.toLowerCase().includes(searchQuery.toLowerCase()) ||
      comm.sender_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      comm.recipient_name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
      <List>
        {filteredComms.map((comm) => (
          <React.Fragment key={comm.id}>
            <ListItemButton
              selected={selectedCommunication?.id === comm.id}
              onClick={() => setSelectedCommunication(comm)}
              sx={{
                backgroundColor: comm.is_read ? 'transparent' : 'action.hover',
                fontWeight: comm.is_read ? 'normal' : 'bold',
              }}
            >
              <ListItemAvatar>
                <Avatar sx={{ bgcolor: getChannelColor(comm.channel) }}>
                  {getChannelIcon(comm.channel)}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: comm.is_read ? 'normal' : 'bold' }}
                    >
                      {comm.direction === 'inbound' ? comm.sender_name : comm.recipient_name}
                    </Typography>
                    {comm.is_important && (
                      <StarIcon fontSize="small" color="warning" />
                    )}
                    <Chip
                      label={comm.status}
                      size="small"
                      color={comm.status === 'failed' ? 'error' : 'default'}
                    />
                    <Chip
                      label={comm.priority}
                      size="small"
                      color={getPriorityColor(comm.priority) as any}
                    />
                  </Box>
                }
                secondary={
                  <Box>
                    {comm.subject && (
                      <Typography variant="body2" sx={{ fontWeight: comm.is_read ? 'normal' : 'bold' }}>
                        {comm.subject}
                      </Typography>
                    )}
                    <Typography variant="body2" color="textSecondary" noWrap>
                      {comm.message}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {new Date(comm.created_at).toLocaleString()}
                    </Typography>
                  </Box>
                }
              />
              <Box sx={{ display: 'flex', gap: 1 }}>
                {!comm.is_read && (
                  <Tooltip title="Mark as read">
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleMarkAsRead(comm.id); }}>
                      <ReadIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Reply">
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleReply(comm); }}>
                    <ReplyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={comm.is_archived ? 'Unarchive' : 'Archive'}>
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleToggleArchive(comm.id); }}>
                    {comm.is_archived ? <UnarchiveIcon fontSize="small" /> : <ArchiveIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setContextItem(comm);
                    setAnchorEl(e.currentTarget);
                  }}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Box>
            </ListItemButton>
            <Divider />
          </React.Fragment>
        ))}
      </List>
    );
  };

  // Render Communication Detail
  const renderCommunicationDetail = () => {
    if (!selectedCommunication) {
      return (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="textSecondary">
            Select a communication to view details
          </Typography>
        </Box>
      );
    }

    return (
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: getChannelColor(selectedCommunication.channel) }}>
                {getChannelIcon(selectedCommunication.channel)}
              </Avatar>
              <Box>
                <Typography variant="h6">
                  {selectedCommunication.direction === 'inbound' 
                    ? `From: ${selectedCommunication.sender_name}`
                    : `To: ${selectedCommunication.recipient_name}`}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {new Date(selectedCommunication.created_at).toLocaleString()}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<ReplyIcon />}
                onClick={() => handleReply(selectedCommunication)}
              >
                Reply
              </Button>
              <IconButton onClick={() => handleToggleArchive(selectedCommunication.id)}>
                {selectedCommunication.is_archived ? <UnarchiveIcon /> : <ArchiveIcon />}
              </IconButton>
              <IconButton onClick={() => handleDelete(selectedCommunication.id)} color="error">
                <DeleteIcon />
              </IconButton>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {selectedCommunication.subject && (
            <Typography variant="h6" sx={{ mb: 2 }}>
              {selectedCommunication.subject}
            </Typography>
          )}

          <Box sx={{ mb: 2 }}>
            <Stack direction="row" spacing={1}>
              <Chip
                icon={getStatusIcon(selectedCommunication.status)}
                label={selectedCommunication.status}
                size="small"
              />
              <Chip
                label={selectedCommunication.priority}
                size="small"
                color={getPriorityColor(selectedCommunication.priority) as any}
              />
              <Chip label={selectedCommunication.channel} size="small" />
              {selectedCommunication.is_important && (
                <Chip icon={<StarIcon />} label="Important" size="small" color="warning" />
              )}
            </Stack>
          </Box>

          <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1, mb: 2 }}>
            {selectedCommunication.html_message ? (
              <div dangerouslySetInnerHTML={{ __html: selectedCommunication.html_message }} />
            ) : (
              <Typography style={{ whiteSpace: 'pre-wrap' }}>
                {selectedCommunication.message}
              </Typography>
            )}
          </Box>

          {selectedCommunication.channel === 'phone' && selectedCommunication.duration_seconds && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="textSecondary">
                Call Duration: {selectedCommunication.formatted_duration}
              </Typography>
              {selectedCommunication.call_recording_url && (
                <Button
                  size="small"
                  startIcon={<PhoneIcon />}
                  href={selectedCommunication.call_recording_url}
                  target="_blank"
                >
                  Listen to Recording
                </Button>
              )}
            </Box>
          )}

          {selectedCommunication.attachments && selectedCommunication.attachments.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Attachments:</Typography>
              <List dense>
                {selectedCommunication.attachments.map((attachment: any, index: number) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      <AttachFileIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={attachment.name}
                      secondary={`${(attachment.size / 1024).toFixed(2)} KB`}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {selectedCommunication.tags && selectedCommunication.tags.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Stack direction="row" spacing={1}>
                {selectedCommunication.tags.map((tag, index) => (
                  <Chip key={index} label={tag} size="small" variant="outlined" />
                ))}
              </Stack>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  // Render Compose Dialog
  const renderComposeDialog = () => (
    <Dialog open={composeDialogOpen} onClose={() => setComposeDialogOpen(false)} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">New Communication</Typography>
          <Button
            startIcon={<TemplateIcon />}
            onClick={() => setTemplateDialogOpen(true)}
          >
            Use Template
          </Button>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid size={{ xs: 12 }}>
            <FormControl fullWidth>
              <InputLabel>Channel</InputLabel>
              <Select
                value={composeData.channel || 'email'}
                onChange={(e) => setComposeData({ ...composeData, channel: e.target.value as CommunicationChannel })}
              >
                <MenuItem value="email">Email</MenuItem>
                <MenuItem value="sms">SMS</MenuItem>
                <MenuItem value="phone">Phone</MenuItem>
                <MenuItem value="whatsapp">WhatsApp</MenuItem>
                <MenuItem value="portal">Portal Message</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <FormControl fullWidth required>
              <InputLabel>Client</InputLabel>
              <Select
                value={composeData.client_id || ''}
                onChange={(e) => handleClientChange(e.target.value as number)}
                label="Client"
              >
                <MenuItem value="">
                  <em>Select a client</em>
                </MenuItem>
                {clients.map((client) => (
                  <MenuItem key={client.id} value={client.id}>
                    {client.name} ({client.email})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth required disabled={!composeData.client_id}>
              <InputLabel>Recipient</InputLabel>
              <Select
                value={composeData.recipient_id || ''}
                onChange={(e) => setComposeData({ ...composeData, recipient_id: e.target.value as number })}
                label="Recipient"
              >
                <MenuItem value="">
                  <em>Select a recipient</em>
                </MenuItem>
                {recipients.map((recipient) => (
                  <MenuItem key={recipient.id} value={recipient.id}>
                    {recipient.name} ({recipient.email})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth disabled={!composeData.client_id}>
              <InputLabel>Project (Optional)</InputLabel>
              <Select
                value={composeData.project_id || ''}
                onChange={(e) => setComposeData({ ...composeData, project_id: e.target.value as number })}
                label="Project (Optional)"
              >
                <MenuItem value="">
                  <em>No project</em>
                </MenuItem>
                {projects.map((project) => (
                  <MenuItem key={project.id} value={project.id}>
                    {project.name} - {project.status}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                value={composeData.priority || 'normal'}
                onChange={(e) => setComposeData({ ...composeData, priority: e.target.value as any })}
              >
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="normal">Normal</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="urgent">Urgent</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {(composeData.channel === 'email' || composeData.channel === 'portal') && (
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Subject"
                value={composeData.subject || ''}
                onChange={(e) => setComposeData({ ...composeData, subject: e.target.value })}
              />
            </Grid>
          )}

          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="Message"
              multiline
              rows={6}
              value={composeData.message || ''}
              onChange={(e) => setComposeData({ ...composeData, message: e.target.value })}
              required
            />
          </Grid>

          {composeData.channel === 'email' && (
            <Grid size={{ xs: 12 }}>
              <Button
                variant="outlined"
                component="label"
                startIcon={<AttachFileIcon />}
              >
                Attach Files
                <input
                  type="file"
                  hidden
                  multiple
                  onChange={(e) => {
                    if (e.target.files) {
                      setAttachments(Array.from(e.target.files));
                    }
                  }}
                />
              </Button>
              {attachments.length > 0 && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {attachments.length} file(s) selected
                </Typography>
              )}
            </Grid>
          )}

          <Grid size={{ xs: 12 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={composeData.is_important || false}
                  onChange={(e) => setComposeData({ ...composeData, is_important: e.target.checked })}
                />
              }
              label="Mark as Important"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setComposeDialogOpen(false)}>Cancel</Button>
        <Button
          variant="contained"
          startIcon={<SendIcon />}
          onClick={handleSendCommunication}
          disabled={loading}
        >
          Send
        </Button>
      </DialogActions>
    </Dialog>
  );

  // Render Template Selection Dialog
  const renderTemplateDialog = () => (
    <Dialog open={templateDialogOpen} onClose={() => setTemplateDialogOpen(false)} maxWidth="md" fullWidth>
      <DialogTitle>Select Template</DialogTitle>
      <DialogContent>
        <List>
          {templates
            .filter(t => !composeData.channel || t.channel === composeData.channel)
            .map((template) => (
              <ListItemButton
                key={template.id}
                onClick={() => handleUseTemplate(template)}
              >
                <ListItemAvatar>
                  <Avatar>{getChannelIcon(template.channel)}</Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={template.name}
                  secondary={
                    <>
                      <Typography variant="body2" color="textSecondary">
                        {template.category} • {template.channel}
                      </Typography>
                      <Typography variant="body2" noWrap>
                        {template.message}
                      </Typography>
                    </>
                  }
                />
                <Chip label={`Used ${template.usage_count}x`} size="small" />
              </ListItemButton>
            ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setTemplateDialogOpen(false)}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );

  // Context Menu
  const renderContextMenu = () => (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={() => setAnchorEl(null)}
    >
      <MenuItem onClick={() => { contextItem && handleMarkAsRead(contextItem.id); setAnchorEl(null); }}>
        Mark as Read
      </MenuItem>
      <MenuItem onClick={() => { contextItem && handleReply(contextItem); setAnchorEl(null); }}>
        Reply
      </MenuItem>
      <MenuItem onClick={() => { contextItem && handleToggleArchive(contextItem.id); setAnchorEl(null); }}>
        {contextItem?.is_archived ? 'Unarchive' : 'Archive'}
      </MenuItem>
      <Divider />
      <MenuItem onClick={() => { contextItem && handleDelete(contextItem.id); setAnchorEl(null); }} sx={{ color: 'error.main' }}>
        Delete
      </MenuItem>
    </Menu>
  );

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Communications</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<SettingsIcon />}
            onClick={() => setPreferencesDialogOpen(true)}
          >
            Preferences
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleCompose()}
          >
            New Message
          </Button>
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Loading */}
      {loading && <CircularProgress sx={{ display: 'block', mx: 'auto', my: 4 }} />}

      {/* Statistics */}
      {!loading && statistics && (
        <Box sx={{ mb: 3 }}>
          {renderStatistics()}
        </Box>
      )}

      {/* Tabs */}
      <Card>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
          <Tab label="Inbox" />
          <Tab label="Threads" />
          <Tab label="Templates" />
        </Tabs>

        <TabPanel value={activeTab} index={0}>
          {/* Filters */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ flexGrow: 1 }}
            />
            
            <ToggleButtonGroup
              value={selectedChannel}
              exclusive
              onChange={(_, value) => value && setSelectedChannel(value)}
              size="small"
            >
              <ToggleButton value="all">All</ToggleButton>
              <ToggleButton value="email"><EmailIcon fontSize="small" /></ToggleButton>
              <ToggleButton value="sms"><SmsIcon fontSize="small" /></ToggleButton>
              <ToggleButton value="phone"><PhoneIcon fontSize="small" /></ToggleButton>
              <ToggleButton value="whatsapp"><WhatsAppIcon fontSize="small" /></ToggleButton>
              <ToggleButton value="portal"><PortalIcon fontSize="small" /></ToggleButton>
            </ToggleButtonGroup>

            <FormControlLabel
              control={
                <Switch
                  checked={showUnreadOnly}
                  onChange={(e) => setShowUnreadOnly(e.target.checked)}
                />
              }
              label="Unread Only"
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={showArchived}
                  onChange={(e) => setShowArchived(e.target.checked)}
                />
              }
              label="Archived"
            />
          </Box>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 5 }}>
              <Paper sx={{ maxHeight: 600, overflow: 'auto' }}>
                {renderCommunicationsList()}
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, md: 7 }}>
              {renderCommunicationDetail()}
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <Paper sx={{ maxHeight: 600, overflow: 'auto' }}>
            {renderThreadsList()}
          </Paper>
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <Box sx={{ mb: 2 }}>
            <Button variant="contained" startIcon={<AddIcon />}>
              Create Template
            </Button>
          </Box>
          <List>
            {templates.map((template) => (
              <ListItem key={template.id}>
                <ListItemAvatar>
                  <Avatar>{getChannelIcon(template.channel)}</Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={template.name}
                  secondary={`${template.category} • Used ${template.usage_count} times`}
                />
                <Button
                  size="small"
                  onClick={() => handleUseTemplate(template)}
                >
                  Use
                </Button>
              </ListItem>
            ))}
          </List>
        </TabPanel>
      </Card>

      {/* Dialogs */}
      {renderComposeDialog()}
      {renderTemplateDialog()}
      {renderContextMenu()}
    </Box>
  );
};

export default Communications;
